import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Briefcase, Clock, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Careers() {
  const [positions, setPositions] = useState([]);
  const [open, setOpen] = useState(null); // selected position object
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    experience_years: "",
    portfolio_url: "",
    cover_letter: "",
  });

  useEffect(() => {
    axios
      .get(`${API}/jobs`)
      .then((r) => setPositions(r.data.positions || []))
      .catch(() => setPositions([]));
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/careers/apply`, {
        ...form,
        position: open.title,
        experience_years: form.experience_years
          ? parseInt(form.experience_years, 10)
          : null,
      });
      toast.success("Application received. We'll be in touch.");
      setOpen(null);
      setForm({
        name: "",
        email: "",
        phone: "",
        experience_years: "",
        portfolio_url: "",
        cover_letter: "",
      });
    } catch (err) {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="careers"
      data-testid="careers-section"
      className="bg-[color:var(--fis-bg-alt)]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
          <div className="md:col-span-7">
            <div className="font-mono-label mb-4">§ 05 — Careers</div>
            <h2 className="font-display text-5xl md:text-7xl">
              Build with us.
            </h2>
          </div>
          <p className="md:col-span-5 text-[color:var(--fis-ink-soft)]">
            We're hiring senior operators who want ownership, not oversight.
            Remote-friendly across APAC, EU and US time zones.
          </p>
        </div>

        <ul className="border-t fis-hairline">
          {positions.map((p) => (
            <li
              key={p.id}
              data-testid={`career-row-${p.id}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center py-8 border-b fis-hairline group"
            >
              <div className="md:col-span-6">
                <h3 className="font-display text-3xl md:text-4xl group-hover:text-[color:var(--fis-accent)] transition-colors">
                  {p.title}
                </h3>
                <p className="mt-2 text-[color:var(--fis-ink-soft)]">
                  {p.description}
                </p>
              </div>
              <div className="md:col-span-4 flex flex-wrap gap-5 text-sm">
                <span className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4" /> {p.department}
                </span>
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {p.location}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" /> {p.type}
                </span>
              </div>
              <div className="md:col-span-2 md:text-right">
                <button
                  onClick={() => setOpen(p)}
                  data-testid={`career-apply-${p.id}`}
                  className="fis-btn fis-btn-outline"
                >
                  Apply →
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-end md:place-items-center p-0 md:p-6"
            onClick={() => setOpen(null)}
            data-testid="career-modal-overlay"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full md:max-w-2xl md:max-h-[85vh] overflow-auto"
            >
              <div className="flex items-start justify-between p-8 border-b fis-hairline">
                <div>
                  <div className="font-mono-label">Apply · {open.id}</div>
                  <h3 className="font-display text-3xl mt-2">{open.title}</h3>
                </div>
                <button
                  onClick={() => setOpen(null)}
                  className="p-2"
                  aria-label="Close"
                  data-testid="career-modal-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={submit} className="p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <input
                    data-testid="apply-name"
                    className="fis-input"
                    placeholder="Full name *"
                    value={form.name}
                    onChange={onChange("name")}
                  />
                  <input
                    data-testid="apply-email"
                    className="fis-input"
                    placeholder="Email *"
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                  />
                  <input
                    data-testid="apply-phone"
                    className="fis-input"
                    placeholder="Phone"
                    value={form.phone}
                    onChange={onChange("phone")}
                  />
                  <input
                    data-testid="apply-experience"
                    className="fis-input"
                    placeholder="Years of experience"
                    type="number"
                    value={form.experience_years}
                    onChange={onChange("experience_years")}
                  />
                </div>
                <input
                  data-testid="apply-portfolio"
                  className="fis-input"
                  placeholder="Portfolio / LinkedIn URL"
                  value={form.portfolio_url}
                  onChange={onChange("portfolio_url")}
                />
                <textarea
                  data-testid="apply-cover-letter"
                  className="fis-input resize-none"
                  rows={4}
                  placeholder="Tell us why this role — in your own words."
                  value={form.cover_letter}
                  onChange={onChange("cover_letter")}
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="apply-submit"
                  className="fis-btn fis-btn-primary w-full justify-center disabled:opacity-60"
                >
                  {submitting ? "Submitting…" : "Submit application"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
